import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { titleCase } from "title-case";
import { Tab, Tabs, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { Inline, Stack } from "components/layout/reusableLayouts";
import useIssuingElement from "hooks/useIssuingElement";
import PaymentCardFace from "components/common/cards/PaymentCardFace";
import RecentTransactionList from "components/common/transactions/RecentTransactionList";
import CardDetailsTab from "components/common/cards/CardDetailsTab";
import CardSpendControlsTab, {
  type CardSpendPolicy,
} from "components/common/cards/CardSpendControlsTab";
import type { CardShipping } from "components/common/cards/CardShippingDetails";
import type CardShippingSpeed from "enums/CardShippingSpeed";
import type { CardSpendingSummary } from "utils/cards/cardRowData";
import CardRequestType from "enums/CardRequestType";
import PendingRequest from "enums/PendingRequest";
import { fullName } from "utils/fullName";
import UserAvatar from "components/common/users/UserAvatar";

type CardPolicyView = CardSpendPolicy & {
  card_activation_date?: string | number | null;
  card_expiration_date?: string | number | null;
  card_cancellation_date?: string | number | null;
};

export interface CardDetailCard {
  id?: string | null;
  status?: string | null;
  customFields?: unknown;
  nickname?: string | null;
  cardProgram?: { id?: string | null; nickname?: string | null } | null;
  type?: string | null;
  user?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profilePicUrl?: string | null;
    acceptedTerms?: boolean | null;
  } | null;
  shipping?: CardShipping | null;
  shippingSpeed?: CardShippingSpeed | null;
  /** An issuer change still in progress, which every other change waits for. */
  pendingChange?: { requestedAt?: number | null } | null;
  spendingSummary?: CardSpendingSummary | null;
  activePolicy?: CardPolicyView | null;
  pendingRequest?: {
    id?: string | null;
    requestType?: string | null;
    requestStatus?: string | null;
    isApprovalAllowed?: boolean | null;
    isDenyAllowed?: boolean | null;
    requestData?: CardPolicyView | null;
    /** Who has already approved, not who may. */
    approverIds?: string[] | null;
    approval?: {
      manager?: {
        approvalStatus?: string | null;
        isRequired?: boolean | null;
      } | null;
    } | null;
  } | null;
}

/**
 * A card with no active policy is one that does not exist yet, so its requested
 * values are its values rather than a proposed change.
 */
export const cardPolicyView = (card?: CardDetailCard | null) => {
  const active = card?.activePolicy;
  const requested = card?.pendingRequest?.requestData;
  const requestType = card?.pendingRequest?.requestType;
  const isNewCardRequest = requestType === CardRequestType.NEW;
  return {
    policy: active ?? requested,
    pendingPolicy: requested,
    hasPendingChange: !!active && !!requested,
    isNewCardRequest,
    requestLabel: isNewCardRequest
      ? PendingRequest.NewCard
      : requestType === CardRequestType.POLICY_UPDATE
        ? PendingRequest.CardLimit
        : "",
  };
};

interface CardDetailBodyProps {
  card?: CardDetailCard | null;
  loading?: boolean;
  /** The buttons this route offers for the card, drawn under its face. */
  actions?: React.ReactNode;
}

export const CardDetails: React.FC<CardDetailBodyProps> = ({
  card,
  loading = false,
  actions,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<
    "details" | "spend" | "transactions"
  >("details");

  const {
    handleViewCard,
    getIssuerFeatures,
    loading: revealing,
    resetCardData,
  } = useIssuingElement();

  // Revealed digits belong to the card that was open, not the next one.
  useEffect(() => {
    resetCardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id]);
  const {
    policy,
    pendingPolicy,
    hasPendingChange,
    isNewCardRequest,
    requestLabel,
  } = cardPolicyView(card);

  const cardholderName = fullName(card?.user);

  return (
    <Stack gap="var(--space-6)">
      <Stack gap="var(--space-6)">
        <Inline gap="var(--space-4)" wrap={false}>
          <UserAvatar size="lg" user={card?.user} />
          <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
            {cardholderName}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "right" }}>
            {titleCase((t(card?.type ?? "") || "").toLowerCase())} {t("card")}
          </Typography>
        </Inline>

        <Box sx={{ marginInline: "auto" }}>
          {/* A new card remounts the face, dropping the previous card's issuer frames. */}
          <PaymentCardFace
            key={card?.id}
            cardId={isNewCardRequest ? undefined : (card?.id ?? undefined)}
            cardData={isNewCardRequest ? undefined : { card }}
            handleViewCard={handleViewCard}
            issuerFeatures={getIssuerFeatures(card?.id)}
            cardLoading={loading || revealing}
          />
        </Box>

        {actions}
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_event, value) => setActiveTab(value)}
        aria-label={t("Card Detail")}
        variant="fullWidth"
      >
        <Tab value="details" label={t("Details")} />
        <Tab value="spend" label={t("Spend controls")} />
        <Tab value="transactions" label={t("Transactions")} />
      </Tabs>

      {activeTab === "details" && (
        <CardDetailsTab
          card={card}
          policy={policy}
          pendingPolicy={pendingPolicy}
          hasPendingChange={hasPendingChange}
          requestTypeLabel={requestLabel ? t(requestLabel) : undefined}
        />
      )}

      {activeTab === "spend" && (
        <CardSpendControlsTab
          policy={policy}
          pendingPolicy={pendingPolicy}
          spendingSummary={card?.spendingSummary}
          hasPendingChange={hasPendingChange}
        />
      )}

      {activeTab === "transactions" && (
        <RecentTransactionList cardId={card?.id ?? undefined} />
      )}
    </Stack>
  );
};

export default CardDetails;
